/**
 * ─────────────────────────────────────────────────────────────
 * SITE CONFIG — static, developer-owned settings.
 *
 * This is the compile-time layer: brand, contact, currency, and
 * *default* opening hours / services. Things the business owner needs
 * to change without a redeploy live in the editable content layer
 * instead (see config/content.registry.ts + lib/content.ts).
 *
 * Rule of thumb:
 *   - changes rarely, set by the developer  → here
 *   - changes often, edited by the owner     → content collection
 * ─────────────────────────────────────────────────────────────
 */
import type { OpeningHour } from '@/lib/types';

export interface SiteConfig {
  name: string;
  legalName: string;
  tagline: string;
  description: string;
  /** Absolute URL of the deployed site (overridden by NEXT_PUBLIC_SITE_URL). */
  url: string;
  locale: string;

  contact: {
    email: string;
    phone: string;
    address: string;
    city: string;
    postalCode: string;
    country: string;
    geo?: { lat: number; lng: number };
  };

  social: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
  };

  /** Brand palette — fed straight into the MUI theme. */
  brand: {
    primary: string;
    secondary: string;
    mode: 'light' | 'dark';
  };

  /** Default opening hours. The owner can override these live via the
   *  `hours` content slot; use resolveHours() to read the effective value. */
  openingHours: OpeningHour[];

  /** Appointment booking defaults. */
  booking: {
    slotMinutes: number;
    horizonDays: number;
    services: string[];
  };

  /** Currency for product prices (ISO 4217). */
  currency: string;
}

export const siteConfig: SiteConfig = {
  name: 'Commerce Local',
  legalName: 'Commerce Local SARL',
  tagline: 'Un service de qualité, près de chez vous',
  description:
    'Commerce Local est votre commerce de proximité proposant des produits et services de qualité. Prenez rendez-vous ou parcourez notre catalogue en ligne.',
  url: process.env.NEXT_PUBLIC_SITE_URL || 'https://www.example.fr',
  locale: 'fr_FR',

  contact: {
    email: 'contact@example.fr',
    phone: '+33 4 76 00 00 00',
    address: '12 rue Principale',
    city: 'Grenoble',
    postalCode: '38000',
    country: 'FR',
    geo: { lat: 45.188, lng: 5.724 },
  },

  social: {
    facebook: 'https://facebook.com/',
    instagram: 'https://instagram.com/',
  },

  brand: {
    primary: '#1565c0',
    secondary: '#ff6f00',
    mode: 'light',
  },

  openingHours: [
    { day: 1, open: '09:00', close: '18:00' },
    { day: 2, open: '09:00', close: '18:00' },
    { day: 3, open: '09:00', close: '18:00' },
    { day: 4, open: '09:00', close: '18:00' },
    { day: 5, open: '09:00', close: '18:00' },
    { day: 6, open: '10:00', close: '16:00' },
  ],

  booking: {
    slotMinutes: 30,
    horizonDays: 30,
    services: ['Consultation', 'Prestation standard', 'Prestation premium'],
  },

  currency: 'EUR',
};

export default siteConfig;
